import type { Account } from '../types';
import { formatUnits, parseUnits } from 'ethers';

interface WithdrawalCollateralTokenInfo {
  token: string;
  base_weight?: number;
  discount_factor?: number;
  user_max_qty?: number;
}

const NEGATIVE_USDC_WITHDRAWAL_BUFFER = 0.002;
const COLLATERAL_WEIGHT_POWER = 4 / 5;

export function isValidTokenDecimals(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

export function normalizeWithdrawalAmount(rawAmount: string, decimals: number): string {
  if (!isValidTokenDecimals(decimals)) {
    throw new Error('Token withdrawal decimals are unavailable');
  }

  const value = rawAmount.trim();
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)) {
    throw new Error('Enter a plain decimal amount');
  }

  const [whole, fraction = ''] = value.split('.');
  if (fraction.length > decimals) {
    throw new Error(`Amount supports at most ${decimals} decimal places`);
  }
  if (!/[1-9]/.test(`${whole}${fraction}`)) {
    throw new Error('Amount must be greater than 0');
  }

  const normalizedFraction = fraction.replace(/0+$/, '');
  return normalizedFraction ? `${whole}.${normalizedFraction}` : whole;
}

export function parseAtomicWithdrawalAmount(rawAmount: string, decimals: number): {
  normalizedRawAmount: string;
  atomicAmount: bigint;
} {
  const normalizedRawAmount = normalizeWithdrawalAmount(rawAmount, decimals);
  return {
    normalizedRawAmount,
    atomicAmount: parseUnits(normalizedRawAmount, decimals),
  };
}

function numberToPlainDecimal(value: number): string {
  const raw = String(value);
  if (!/[eE]/.test(raw)) return raw;

  const [coefficient, exponentText] = raw.toLowerCase().split('e');
  const exponent = Number(exponentText);
  const [whole, fraction = ''] = coefficient.split('.');
  const digits = `${whole}${fraction}`;
  const decimalPosition = whole.length + exponent;
  if (decimalPosition <= 0) return `0.${'0'.repeat(-decimalPosition)}${digits}`;
  if (decimalPosition >= digits.length) return digits.padEnd(decimalPosition, '0');
  return `${digits.slice(0, decimalPosition)}.${digits.slice(decimalPosition)}`;
}

function parseAtomicMetadataAmount(value: unknown, decimals: number): bigint {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error('Token withdrawal metadata is unavailable');
  }

  const rawValue = numberToPlainDecimal(value);
  if (!/^\d+(?:\.\d+)?$/.test(rawValue)) {
    throw new Error('Token withdrawal metadata is unavailable');
  }

  try {
    return parseUnits(rawValue, decimals);
  } catch {
    throw new Error('Token withdrawal metadata is unavailable');
  }
}

export function getWalletWithdrawalAmountError(
  rawAmount: string,
  decimals: number,
  minimumWithdrawAmount: unknown,
  withdrawalFee: unknown,
): string | null {
  try {
    const { atomicAmount } = parseAtomicWithdrawalAmount(rawAmount, decimals);
    const minimumAtomic = parseAtomicMetadataAmount(minimumWithdrawAmount, decimals);
    const feeAtomic = parseAtomicMetadataAmount(withdrawalFee, decimals);
    const requiredAtomic = minimumAtomic + feeAtomic;
    if (atomicAmount < requiredAtomic) {
      return `Amount must be at least ${formatUnits(requiredAtomic, decimals)} (minimum plus withdrawal fee)`;
    }
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'Token withdrawal metadata is unavailable';
  }
}

export function formatFullWithdrawalAmount(available: number, decimals: number): string {
  if (!isValidTokenDecimals(decimals)) {
    throw new Error('Token withdrawal decimals are unavailable');
  }
  if (!Number.isFinite(available) || available < 0) {
    throw new Error('Available balance is unavailable');
  }

  const [whole, fraction = ''] = numberToPlainDecimal(available).split('.');
  const truncatedFraction = fraction.slice(0, decimals).replace(/0+$/, '');
  return truncatedFraction ? `${whole}.${truncatedFraction}` : whole;
}

export function isSubAtomicWithdrawalRemainder(remaining: number, decimals: number): boolean {
  return isValidTokenDecimals(decimals)
    && Number.isFinite(remaining)
    && remaining > 0
    && remaining < 10 ** -decimals;
}

function comparePlainDecimals(left: string, right: string): number {
  const [leftWhole, leftFraction = ''] = left.split('.');
  const [rightWhole, rightFraction = ''] = right.split('.');
  if (leftWhole.length !== rightWhole.length) return leftWhole.length - rightWhole.length;
  const wholeComparison = leftWhole.localeCompare(rightWhole);
  if (wholeComparison !== 0) return wholeComparison;
  const width = Math.max(leftFraction.length, rightFraction.length);
  return leftFraction.padEnd(width, '0').localeCompare(rightFraction.padEnd(width, '0'));
}

export function isWithdrawalAmountAboveAvailable(rawAmount: string, available: number, decimals: number): boolean {
  const normalizedRawAmount = normalizeWithdrawalAmount(rawAmount, decimals);
  return comparePlainDecimals(normalizedRawAmount, numberToPlainDecimal(available)) > 0;
}

function getCollateralWeight(
  holding: number,
  indexPrice: number,
  tokenInfo: WithdrawalCollateralTokenInfo | undefined,
): number {
  const baseWeight = tokenInfo?.base_weight ?? 0;
  const discountFactor = tokenInfo?.discount_factor ?? 0;
  const configuredCap = tokenInfo?.user_max_qty;
  const collateralCap = configuredCap === undefined || configuredCap === -1 ? holding : configuredCap;
  const collateralQty = Math.min(holding, collateralCap);
  const notional = Math.abs(collateralQty * indexPrice);
  const dynamicWeight = 1.2 / (1 + discountFactor * notional ** COLLATERAL_WEIGHT_POWER);
  return Math.min(dynamicWeight, baseWeight);
}

/** Mirrors the SDK useMaxWithdrawal calculation after orders, positions, and PnL are cleared. */
export function getMaxWithdrawalAmounts(
  account: Pick<Account, 'holdings' | 'unsettledPnl'>,
  tokenInfoMap: Map<string, WithdrawalCollateralTokenInfo>,
  tokenPriceMap: Map<string, number>,
): Map<string, number> {
  const usdcHolding = account.holdings.find((holding) => holding.token === 'USDC')?.holding ?? 0;
  const collateralWeights = new Map<string, number>();
  let totalCollateral = usdcHolding + account.unsettledPnl;

  for (const holding of account.holdings) {
    if (holding.token === 'USDC' || holding.holding <= 0) continue;
    const indexPrice = tokenPriceMap.get(holding.token) ?? 0;
    const tokenInfo = tokenInfoMap.get(holding.token);
    const weight = getCollateralWeight(holding.holding, indexPrice, tokenInfo);
    const configuredCap = tokenInfo?.user_max_qty;
    const collateralCap = configuredCap === undefined || configuredCap === -1 ? holding.holding : configuredCap;
    collateralWeights.set(holding.token, weight);
    totalCollateral += Math.min(holding.holding, collateralCap) * indexPrice * weight;
  }

  const freeCollateral = Math.max(0, totalCollateral);
  const amounts = new Map<string, number>();
  for (const holding of account.holdings) {
    const unfrozenHolding = Math.max(0, holding.holding - (holding.frozen ?? 0));
    if (holding.token === 'USDC') {
      const sdkMaximum = Math.max(0, Math.min(usdcHolding, freeCollateral - Math.max(account.unsettledPnl, 0)));
      amounts.set(holding.token, Math.min(unfrozenHolding, sdkMaximum));
      continue;
    }

    // Once earlier offboarding steps have cleared margin usage, a non-negative
    // USDC balance makes the SDK maximum equal to the unfrozen token holding.
    if (usdcHolding >= 0) {
      amounts.set(holding.token, unfrozenHolding);
      continue;
    }

    const indexPrice = tokenPriceMap.get(holding.token) ?? 0;
    const weight = collateralWeights.get(holding.token) ?? 0;
    const buffer = usdcHolding < 0 ? 1 + NEGATIVE_USDC_WITHDRAWAL_BUFFER : 1;
    const denominator = indexPrice * weight * buffer;
    const sdkMaximum = denominator > 0 ? Math.min(holding.holding, freeCollateral / denominator) : 0;
    amounts.set(holding.token, Math.max(0, Math.min(unfrozenHolding, sdkMaximum)));
  }

  return amounts;
}

function formatTokenAmount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(value);
}

export function getNegativeUsdcBalance(account: Pick<Account, 'holdings' | 'usdcBalance'>): number {
  const usdcHolding = account.holdings.find((holding) => holding.token === 'USDC')?.holding;
  const usdcBalance = Math.min(account.usdcBalance, usdcHolding ?? account.usdcBalance);
  return usdcBalance < 0 ? Math.abs(usdcBalance) : 0;
}

export function getNegativeUsdcCollateralMessage(account: Pick<Account, 'holdings' | 'usdcBalance'>, token?: string): string | null {
  const liability = getNegativeUsdcBalance(account);
  if (liability <= 0) return null;

  const subject = token && token !== 'USDC' ? `${token} is` : 'Positive asset balances are';
  const result = token && token !== 'USDC' ? `the full ${token} balance may not be withdrawable` : 'the full balances may not be withdrawable';

  return `This account has a negative USDC balance of ${formatTokenAmount(liability)} USDC. ${subject} being used as collateral to cover it, so ${result}. You can convert collateral to cover the balance or skip conversion and withdraw the available amount.`;
}

export function getWithdrawalErrorMessage(error: unknown, account: Pick<Account, 'holdings' | 'usdcBalance'>, token: string): string {
  const fallback = error instanceof Error ? error.message : 'Step failed';
  if (token === 'USDC' || !/margin is occupied|available balance/i.test(fallback)) {
    return fallback;
  }

  return getNegativeUsdcCollateralMessage(account, token) ?? fallback;
}
